import { CurrencyDisplay, LinkWithIcon } from "../../../components";
import { HookFormSelect, SubmitButton, TextInput } from "../../../hook-form";
import {
  IHookForm,
  IInitTransactionRequest,
} from "../../../interfaces-and-types";

import amexIcon from "../../../assets/icons/card-vendor-icons/amex.png";
import discoverIcon from "../../../assets/icons/card-vendor-icons/discover.png";
import mastercardIcon from "../../../assets/icons/card-vendor-icons/mastercard.png";
import visaIcon from "../../../assets/icons/card-vendor-icons/visa.png";

import switchAppIcon from "../../../assets/images/switchapp-logo.svg";
import flutterwaveIcon from "../../../assets/icons/flutterwave.svg";
import paystackIcon from "../../../assets/icons/paystack.svg";
import squareIcon from "../../../assets/icons/square-payments.svg";
import stripeIcon from "../../../assets/icons/stripe.svg";
import { countries } from "../../../constants";
import { formatAsCurrency } from "../../../utils";
import { IPaymentLinkPreviewProps } from "../PaymentLinkPreview/PaymentLinkPreview";
import { ProductInPaymentLinkDisplay } from "./ProductInPaymentLinkDisplay";

export const View = ({
  currency,
  amount,
  merchant_name,
  paymentLinkToPreview,
  productsInPaymentLink,
  errors: errors,
  handleSubmit: handleSubmit,
  register: register,
}: IHookForm<IInitTransactionRequest> &
  IPaymentLinkPreviewProps & {
    currency: string;
    amount: number;
    merchant_name: string;
  }) => {
  const CardIcons = () => {
    return (
      <div className="mr-5">
        <img
          src={mastercardIcon}
          alt="card vendor logo"
          className="card-vendor--logo"
        />
        <img
          src={visaIcon}
          alt="card vendor logo"
          className="card-vendor--logo bordered-icon"
        />
        <img
          src={amexIcon}
          alt="card vendor logo"
          className="card-vendor--logo bordered-icon"
        />
        <img
          src={discoverIcon}
          alt="card vendor logo"
          className="card-vendor--logo bordered-icon"
        />
      </div>
    );
  };

  const SocialMediaLink = ({
    link_url,
    imageUrl,
    socialMediaName,
    text = "view more about this item and other available products",
  }: {
    link_url: string;
    imageUrl: string;
    socialMediaName: string;
    text?: string;
  }) => (
    <div className="social-icon--link">
      <a href={link_url} target="_blank">
        <div className="flex-container align-items--end">
          <img
            src={imageUrl}
            className="social-icon"
            alt={`${socialMediaName} icon`}
          />
          {text}
        </div>
      </a>
    </div>
  );

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className="inline-payment-link-preview--container flex-container justify-items--center align-items--center">
          <div>
            <div className="flex-container flex-break--small justify-content--between bb mb-5">
              <div className="">
                <img
                  src={switchAppIcon}
                  alt="payment methods"
                  className="switchapp-logo"
                />
              </div>

              <div className="flex-box flex-container justify-content--between align-items--end">
                <div className="d-none d-md-block" />

                <h3 className="font-ctm--bold">
                  <CurrencyDisplay
                    currencyOrSymbol={currency}
                    amount={amount}
                  />
                </h3>
              </div>
            </div>

            <div className="flex-container flex-break--small justify-content--between mb-5">
              <span className="form-section--title">Pay {merchant_name}</span>
            </div>

            <div className="flex-container">
              <div className="flex-box col left-section flex-container">
                <div className="flex-box flex-container col products-list--container">
                  {paymentLinkToPreview.line_items.map((lineItem) => {
                    const productToDisplay = productsInPaymentLink.find(
                      (p) => p.id === lineItem.product
                    );

                    return !productToDisplay ? null : (
                      <ProductInPaymentLinkDisplay
                        key={lineItem.product}
                        product={productToDisplay}
                        quantity={lineItem.qty}
                      />
                    );
                  })}
                </div>

                <div className="social-icons--container align-self--end">
                  {!paymentLinkToPreview.enable_social_media_links ? null : (
                    <>
                      {paymentLinkToPreview.instagram_url ? (
                        <SocialMediaLink
                          link_url={paymentLinkToPreview.instagram_url}
                          imageUrl={`/social-icons/instagram.png`}
                          socialMediaName={"instagram"}
                        />
                      ) : null}

                      {paymentLinkToPreview.facebook_url ? (
                        <SocialMediaLink
                          link_url={paymentLinkToPreview.facebook_url}
                          imageUrl={`/social-icons/facebook.png`}
                          socialMediaName={"facebook"}
                        />
                      ) : null}

                      {paymentLinkToPreview.whatsapp_url ? (
                        <SocialMediaLink
                          link_url={paymentLinkToPreview.whatsapp_url}
                          imageUrl={`/social-icons/whatsapp.png`}
                          socialMediaName={"WhatsApp"}
                        />
                      ) : null}
                    </>
                  )}
                </div>
              </div>

              <div className="flex-divider--vertical d-none d-lg-block" />

              <div className="flex-container col align-items--stretch">
                <TextInput
                  name="email"
                  label="Email"
                  type="text"
                  register={register}
                  errors={errors}
                  disabled
                />

                {!paymentLinkToPreview.require_customer_full_name ? null : (
                  <TextInput
                    name="full_name"
                    label="Full Name"
                    type="text"
                    register={register}
                    errors={errors}
                    disabled
                  />
                )}

                {!paymentLinkToPreview.require_customer_address ? null : (
                  <TextInput
                    name="address"
                    label="Address"
                    type="text"
                    register={register}
                    errors={errors}
                    disabled
                  />
                )}

                {!paymentLinkToPreview.require_customer_phone_number ? null : (
                  <TextInput
                    name="phone_number"
                    label="Phone Number"
                    type="text"
                    register={register}
                    errors={errors}
                    disabled
                  />
                )}

                <TextInput
                  name="cardHolderName"
                  label="Card Holder Name"
                  type="text"
                  register={register}
                  errors={errors}
                  disabled
                />

                <TextInput
                  name="card_number"
                  label="Card Details"
                  // placeholder="Card Number"
                  placeholder="1234 1234 1234 1234"
                  type="text"
                  iconRight={<CardIcons />}
                  // containerClassName="card-input--field"
                  className="card-number"
                  register={register}
                  errors={errors}
                  disabled
                />

                <div className="flex-container">
                  <TextInput
                    name="cardExpiryDate"
                    label=""
                    // placeholder="Expiry Date"
                    placeholder="MM/YY"
                    type="text"
                    containerClassName="card-input--field"
                    className="card-expiry-date"
                    register={register}
                    errors={errors}
                    disabled
                  />
                  <TextInput
                    name="cardCvv"
                    label=""
                    placeholder="CVV"
                    // helperText="3 digits on back of your card"
                    type="text"
                    containerClassName="card-input--field"
                    className="card-cvv"
                    register={register}
                    errors={errors}
                    disabled
                  />
                </div>

                <HookFormSelect
                  name="country"
                  label="Country Or Region"
                  selectItems={[]}
                  selectGroupedItems={countries}
                  register={register}
                  errors={errors}
                  disabled
                />

                <div className="flex-container buttons-container mt-5">
                  <SubmitButton disabled className=" onboarding-submit--button">
                    Pay
                  </SubmitButton>
                </div>

                {/* <div className="flex-container col align-items--center mt-5">
                  <span className="pay-with-card">
                    Or use a payment gateway
                  </span>
                </div>

                <LinkWithIcon
                  iconLeft={<img src={flutterwaveIcon} alt="payment methods" />}
                  title="Flutterwave"
                  subtitle="Complete Your Payment With Flutterwave"
                  urlTarget="#"
                  asButton
                  onButtonClick={() => {}}
                />

                <LinkWithIcon
                  iconLeft={<img src={stripeIcon} alt="payment methods" />}
                  title="Stripe"
                  subtitle="Complete Your Payment With Stripe"
                  urlTarget="#"
                  asButton
                  onButtonClick={() => {}}
                />

                <LinkWithIcon
                  iconLeft={<img src={paystackIcon} alt="payment methods" />}
                  title="Paystack"
                  subtitle="Complete Your Payment With Paystack"
                  urlTarget="#"
                  asButton
                  onButtonClick={() => {}}
                />

                <LinkWithIcon
                  iconLeft={<img src={squareIcon} alt="payment methods" />}
                  title="Square"
                  subtitle="Complete Your Payment With Sqaure"
                  urlTarget="#"
                  asButton
                  onButtonClick={() => {}}
                /> */}
              </div>
            </div>

            <div className="company-name--footer flex-container align-items--center">
              <div className="company-name mr-5">
                <span className="powered-by">Powered By</span>{" "}
                <img
                  src={switchAppIcon}
                  alt="payment methods"
                  className="footer-logo"
                />
              </div>
            </div>
          </div>
          {/* </FormContainer> */}
        </div>
      </form>
    </>
  );
};
